import first from 'lodash/first';

import { convertToResourceKeyFromPath } from '../utils/converter';
import { createIntersectionObserver } from '../utils/intersction-observer';
import { getResources } from '../utils/resource-timings';
import { browserPerformanceTimeOrigin } from '../utils/time';

export type ATFElementSelector = keyof HTMLElementTagNameMap | `${keyof HTMLElementTagNameMap}[data-atf]`;

export interface ATFResponse {
  duration: number;
  timestamp: number;
  highResTimestamp: number;
}

export const calculateATFByTags = (tags: Array<ATFElementSelector>) => {
  return calculateATFByElements(document.querySelectorAll(tags.join(',')));
};

export const calculateATFByElements = (
  elements: Element[] | HTMLCollectionOf<HTMLElement> | NodeListOf<HTMLElement>,
): Promise<ATFResponse> => {
  return new Promise((resolve, reject) => {
    const timeOrigin = browserPerformanceTimeOrigin ?? Date.now() - performance.now();
    const resources = getResources();
    const targets = Array.from(elements);

    const { register } = createIntersectionObserver(
      (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        const intersectingTargets = entries
          .filter((entry) => {
            observer.unobserve(entry.target);
            return entry.isIntersecting;
          })
          .map((entry) => {
            return entry.target;
          });

        if (intersectingTargets.length === 0) {
          reject(new Error('[atf] 측정할 요소 없음'));
          return;
        }

        const fetchStart = intersectingTargets.reduce((prev, target) => {
          if (target instanceof HTMLImageElement) {
            return getImageFetchStart(prev, target, resources);
          }

          if (target instanceof HTMLVideoElement) {
            return getVideoFetchStart(prev, target, resources);
          }

          return prev;
        }, Infinity);

        const responseEnd = intersectingTargets.reduce((prev, target) => {
          if (target instanceof HTMLImageElement) {
            return getImageResponseEnd(prev, target, resources);
          }

          if (target instanceof HTMLVideoElement) {
            return getVideoResponseEnd(prev, target, resources);
          }

          return prev;
        }, 0);

        const duration = responseEnd - fetchStart;

        return resolve({
          duration,
          timestamp: Math.floor(timeOrigin + responseEnd),
          highResTimestamp: Math.floor(responseEnd),
        });
      },
      {
        threshold: 0,
      },
    );

    targets.map(register);
  });
};

const getImageFetchStart = (
  prev: number,
  element: HTMLImageElement,
  resources: Record<string, PerformanceResourceTiming>,
) => {
  if (element.src.includes('atf=false')) {
    return prev;
  }

  const imageKey = convertToResourceKeyFromPath(element.src);
  const fetchStart = Math.floor(resources[imageKey]?.fetchStart ?? 0);
  return Math.min(prev, fetchStart);
};

const getVideoFetchStart = (
  prev: number,
  element: HTMLVideoElement,
  resources: Record<string, PerformanceResourceTiming>,
) => {
  const videoKey = convertToResourceKeyFromPath(element.poster);

  if (resources[videoKey]) {
    const fetchStart = Math.floor(resources[videoKey]?.fetchStart ?? 0);
    return Math.min(prev, fetchStart);
  }

  const videoSource = first(element.getElementsByTagName('source'));

  if (!videoSource?.src) {
    return prev;
  }

  const videoSourceKey = convertToResourceKeyFromPath(videoSource.src);
  const fetchStart = Math.floor(resources[videoSourceKey]?.fetchStart ?? 0);
  return Math.min(prev, fetchStart);
};

const getImageResponseEnd = (
  prev: number,
  element: HTMLImageElement,
  resources: Record<string, PerformanceResourceTiming>,
) => {
  if (element.src.includes('atf=false')) {
    return prev;
  }

  const imageKey = convertToResourceKeyFromPath(element.src);
  const responseEnd = Math.floor(resources[imageKey]?.responseEnd ?? 0);
  return Math.max(prev, responseEnd);
};

const getVideoResponseEnd = (
  prev: number,
  element: HTMLVideoElement,
  resources: Record<string, PerformanceResourceTiming>,
) => {
  const videoKey = convertToResourceKeyFromPath(element.poster);

  if (resources[videoKey]) {
    const responseEnd = Math.floor(resources[videoKey]?.responseEnd ?? 0);
    return Math.max(prev, responseEnd);
  }

  const videoSource = first(element.getElementsByTagName('source'));

  if (!videoSource?.src) {
    return prev;
  }

  const videoSourceKey = convertToResourceKeyFromPath(videoSource.src);
  const responseEnd = Math.floor(resources[videoSourceKey]?.responseEnd ?? 0);
  return Math.max(prev, responseEnd);
};
